import { BasicPoint, Color, EaseSegment, Emitter, EmitterConfig, RandNumber, SimpleEase, ValueList } from "pixi-particles";
import { Texture, utils as PixiUtils, Point, Container } from "pixi.js";
import App from "../App";
import Layout from "../utils/Layout";
import Utils from "../utils/Utils";
import TrailPathConfig from "./TrailPathConfig";

/**
 * A class, used to create and maintain path driven particles.
 *
 * @export
 * @class TrailPath
 */
export class TrailPath {
    private readonly trailPathConfig: TrailPathConfig;

    /**
     * Gets or sets the trail path color.
     *
     * @type {Color}
     * @memberof TrailPath
     */
    public color: Color;

    /**
     * Gets or sets the trail path quality in range 0.1 - 1.0.
     * Max quality may cause performance issues.
     *
     * @type {number}
     * @memberof TrailPath
     */
    public quality: number;

    public config: TrailEmitterConfig;

    private pts: Point[];
    private emitter: Emitter;
    private targetPos: Point;
    private emitterPos: Point;
    private callback: () => void;
    private isActive: boolean;
    private isDestroyed: boolean;
    private texture: Texture;

    /**
     * Initializes a new instance of a TrailPath class.
     * @param container The pixi container, where to run the trail emitter.
     * Make sure you pass fast containers like LinkedListContainer or ParticleContainer.
     * @param config The trail emitter config to apply.
     * @param onComplete The callback function to call, when the trail ends.
     */
    constructor(container: Container, config: TrailEmitterConfig, onComplete: () => void, texture: Texture = undefined, trailConfig: TrailPathConfig = undefined) {
        if (!trailConfig) {
            trailConfig = new TrailPathConfig();
        }

        this.trailPathConfig = trailConfig;
        this.isActive = false;
        this.callback = onComplete;
        
        if (!texture) {
            texture = this.createTexture(0, 8 * Layout.globalScale, App.instance.renderer.resolution);
        }

        this.texture = texture;
        this.emitter = new Emitter(container, [texture], config);
        this.color = {
            r: this.emitter.startColor.value.r,
            g: this.emitter.startColor.value.g,
            b: this.emitter.startColor.value.b
        } as Color;

        this.config = config; 
        this.quality = 1.0;
    }

    /**
     * Gets the trail path points.
     *
     * @readonly
     * @type {Point[]}
     * @memberof TrailPath
     */
    public get points(): Point[] {
        return this.pts;
    }

    /**
     * Checks whether the trail is active or not.
     *
     * @readonly
     * @memberof TrailPath
     */
    public get active() {
        return this.isActive;
    }

    /**
     * Checks whether the trail has been destroyed or not.
     *
     * @readonly
     * @type {boolean}
     * @memberof TrailPath
     */
    public get destroyed(): boolean {
        return this.isDestroyed;
    }

    /**
     * Destroys the trail path and its resources.
     *
     * @return {*}  {void}
     * @memberof TrailPath
     */
    public destroy(): void {
        if (this.isDestroyed) {
            return;
        }

        this.stop(false);
        this.emitter.destroy();
        this.isDestroyed = true;
    }

    /**
     * Starts the trail path.
     *
     * @param {Point[]} points The points, which defines the path of the trail.
     * @memberof TrailPath
     */
    public start(points: Point[]) {
        if (!points) {
            throw new Error("points can not be null or undefined");
        }

        if (points.length < 2) {
            throw new Error("points must be at least 2!");
        }

        this.pts = new Array<Point>();
        for (let i = 0; i < points.length; i++) {
            const point = points[i];
            this.pts.push(new Point(point.x, point.y));
        }

        this.emitter.init(this.texture, this.config);
        this.emitterPos = this.pts[0];
        this.targetPos = this.pts[1];

        this.emitter.resetPositionTracking();
        App.instance.ticker.add(this.onTick, this);
        this.isActive = true;
    }

    /**
     * Stops the trail path execution.
     *
     * @param {boolean} raiseEvent Pass true to execute the callback, passed at the constructor,
     * otherwise false.
     * @return {*} 
     * @memberof TrailPath
     */
    public stop(raiseEvent: boolean) {
        if (this.isDestroyed) {
            return;
        }

        if (!this.isActive) {
            return;
        }

        App.instance.ticker.remove(this.onTick, this);
        this.emitterPos = new Point(-100, -100);
        this.targetPos = this.emitterPos;
        this.emitter.resetPositionTracking();
        this.emitter.updateOwnerPos(this.emitterPos.x, this.emitterPos.y);
        this.isActive = false;

        if (raiseEvent && this.callback) {
            this.callback();
        }
    }

    private createTexture(r1: number, r2: number, resolution: number) {
        const c = (r2 + 1) * resolution;
        r1 *= resolution;
        r2 *= resolution;

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = canvas.height = Math.max(8, (c * 2) * Layout.globalScale);

        const gradient = context.createRadialGradient(c, c, r1, c, c, r2);
        gradient.addColorStop(0, "rgba(255,255,255,1)");
        gradient.addColorStop(1, "rgba(255,255,255,0)");

        context.fillStyle = gradient;
        context.fillRect(0, 0, canvas.width, canvas.height);

        return Texture.from(canvas);
    }

    private onTick(deltaTime: number) {
        if ((deltaTime <= this.trailPathConfig.maxTimeBetweenPointsInMilliseconds || this.targetPos == this.pts[this.pts.length - 1]) &&
            !this.isCloseEnough(this.emitterPos, this.targetPos, 15)) {
            const dt = 1 - Math.pow(1 - this.trailPathConfig.sharpness, deltaTime);
            const dx = this.targetPos.x - this.emitterPos.x;
            const dy = this.targetPos.y - this.emitterPos.y;

            if (Math.abs(dx) > this.trailPathConfig.minDelta) {
                this.emitterPos.x += dx * dt;
            } else {
                this.emitterPos.x = this.targetPos.x;
            }

            if (Math.abs(dy) > this.trailPathConfig.minDelta) {
                this.emitterPos.y += dy * dt;
            } else {
                this.emitterPos.y = this.targetPos.y;
            }

            this.emitter.updateOwnerPos(this.emitterPos.x, this.emitterPos.y);
        } else {
            let pointIndex = this.pts.indexOf(this.targetPos);
            if (pointIndex == this.pts.length - 1) {
                this.stop(true);
            } else {
                this.emitterPos = this.pts[pointIndex];
                this.targetPos = this.pts[pointIndex + 1];
                let newPos = Utils.getPositionAlongTheLine(this.emitterPos.x, this.emitterPos.y, this.targetPos.x, this.targetPos.y, 0.10);
                this.emitterPos.x = newPos.x;
                this.emitterPos.y = newPos.y;
                this.emitter.updateOwnerPos(this.emitterPos.x, this.emitterPos.y);
            }
        }
    }

    private isCloseEnough(p1: Point, p2: Point, tolerance: number): boolean {
        return Math.abs(p1.x - p2.x) < tolerance && Math.abs(p1.y - p2.y) < tolerance;
    }
}

export class TrailEmitterConfig implements EmitterConfig { 

    /**
     * Creates a new config, by providing json, typically generated at http://pixijs.io/pixi-particles-editor/#
     *
     * @static
     * @param {string} json The json text content
     * @return {*}  {TrailEmitterConfig} New trail emitter config
     * @memberof TrailEmitterConfig
     */
    public static fromJson(json: string): TrailEmitterConfig {
        let obj = JSON.parse(json);
        let config = new TrailEmitterConfig({r: 255, g: 255, b: 255, a: 255}, 1.0);

        config.autoUpdate = config.autoUpdate;
        config.alpha = { list: [{ value: obj.alpha.start, time: 0 }, { value: obj.alpha.end, time: 1 }] };
        config.scale = { list: [{ value: obj.scale.start, time: 0 }, { value: obj.scale.end, time: 1 }] };
        config.minimumScaleMultiplier = obj.scale.minimumScaleMultiplier;
        config.color = { list: [{ value: obj.color.start, time: 0 }, { value: obj.color.end, time: 1 }] };
        config.acceleration = { x: obj.acceleration.x, y: obj.acceleration.y };
        config.speed = { list: [{ value: obj.speed.start, time: 0 }, { value: obj.speed.end, time: 1 }] };
        config.minimumSpeedMultiplier = obj.speed.minimumSpeedMultiplier;
        config.maxSpeed = obj.maxSpeed;
        config.startRotation = { min: obj.startRotation.min, max: obj.startRotation.max };
        config.noRotation = obj.noRotation;
        config.rotationSpeed = { min: obj.rotationSpeed.min, max: obj.rotationSpeed.max };
        config.lifetime = { min: obj.lifetime.min, max: obj.lifetime.max };
        config.blendMode = obj.blendMode;
        config.frequency = obj.frequency;
        config.maxParticles = obj.maxParticles;
        config.emitterLifetime = obj.emitterLifetime;
        config.pos = { x: obj.pos.x, y: obj.pos.y };
        config.addAtBack = obj.addAtBack;
        config.spawnType = obj.spawnType;
        config.particlesPerWave = obj.particlesPerWave;
        config.particleSpacing = obj.particleSpacing;
        config.angleStart = obj.angleStart;

        return config;
    }

    alpha?: ValueList<number>;
    speed?: ValueList<number>;
    minimumSpeedMultiplier?: number;
    maxSpeed?: number;
    acceleration?: { x: number; y: number; };
    scale?: ValueList<number>;
    minimumScaleMultiplier?: number;
    color?: ValueList<string>;
    startRotation?: RandNumber;
    noRotation?: boolean;
    rotationSpeed?: RandNumber;
    rotationAcceleration?: number;
    lifetime: RandNumber;
    blendMode?: string;
    ease?: SimpleEase | EaseSegment[];
    extraData?: any;
    particlesPerWave?: number;
    spawnType?: string;
    spawnRect?: { x: number; y: number; w: number; h: number; };
    spawnCircle?: { x: number; y: number; r: number; minR?: number; };
    particleSpacing?: number;
    angleStart?: number;
    spawnPolygon?: BasicPoint[] | BasicPoint[][];
    frequency: number;
    spawnChance?: number;
    emitterLifetime?: number;
    maxParticles?: number;
    addAtBack?: boolean;
    pos: { x: number; y: number; };
    emit?: boolean;
    autoUpdate?: boolean;
    orderedArt?: boolean;

    /**
     * Creates an instance of TrailEmitterConfig.
     * @param {string} color The trail path color. Example {r: 255, g: 255, b: 255, a: 255} or Color.fromBytes(255, 255, 255, 255).
     * @param {number} quality The trail path quality in range 0.1 - 1.0.
     * Higher value means better quality, but decreases the performance.
     * @memberof TrailEmitterConfig
     */
    constructor(color: Color, quality: number) {
        // Clamping the quality in range 0.1 - 1.0
        quality = Math.min(quality, 1.0);
        quality = Math.max(0.1, quality);

        this.autoUpdate = true;
        this.alpha = { list: [{ value: 0.8, time: 0 }, { value: 0.15, time: 1 }] };
        this.scale = { list: [{ value: 1, time: 0 }, { value: 0.2, time: 1 }] };
        this.minimumScaleMultiplier = 1;
        let colorString = PixiUtils.hex2string(PixiUtils.rgb2hex([color.r / 255, color.g / 255, color.b / 255]));
        this.color = { list: [{ value: colorString, time: 0 }] };
        this.acceleration = { x: 0, y: 0 };
        this.maxSpeed = 0;
        this.startRotation = { min: 0, max: 0 };
        this.noRotation = true;
        this.rotationSpeed = { min: 0, max: 0 };
        this.lifetime = { min: 1.0, max: 1.0 };
        this.blendMode = "normal";

        // max = 0.002;
        // avg = 0.008;
        // min = 0.200;
        this.frequency = (0.002 / quality) / quality;
        this.emitterLifetime = -1;
        this.maxParticles = 1000;
        this.pos = { x: 0, y: 0, };
        this.addAtBack = false;
        this.spawnType = "point";
    }
}